import PropTypes from 'prop-types';
import Link from 'next/link';

const RELATION_LABELS = {
  causes:              'Causes',
  caused_by:           'Caused by',
  reinforces:          'Reinforces',
  reinforced_by:       'Reinforced by',
  enables:             'Enables',
  enabled_by:          'Enabled by',
  inhibits:            'Inhibits',
  inhibited_by:        'Inhibited by',
  supports:            'Supports',
  supported_by:        'Supported by',
  contradicts:         'Contradicts',
  contradicted_by:     'Contradicted by',
  is_prerequisite_for: 'Is prerequisite for',
  requires:            'Requires',
  is_example_of:       'Is example of',
  exemplified_by:      'Exemplified by',
  is_exception_to:     'Is exception to',
  has_exception:       'Has exception',
  part_of:             'Part of',
  contains:            'Contains',
  derived_from:        'Derived from',
  source_of:           'Source of',
  generalizes:         'Generalizes',
  specializes:         'Specializes',
  legitimizes:         'Legitimizes',
  legitimized_by:      'Legitimized by',
  normalizes:          'Normalizes',
  normalized_by:       'Normalized by',
  challenges:          'Challenges',
  challenged_by:       'Challenged by',
  similar_to:          'Similar to',
  contrasts_with:      'Contrasts with',
  co_occurs_with:      'Co-occurs with',
};

const RelationItem = ({ relationType, topic, confidence, scope, isUserHypothesis, note }) => (
  <div className={`topic-relation topic-relation--${confidence}`}>
    <div className="topic-relation__edge">
      <span className="topic-relation__type text-label">
        {RELATION_LABELS[relationType] || relationType}
      </span>
      {confidence !== 'established' && (
        <span className="topic-relation__confidence text-label">
          {confidence === 'working_hypothesis' ? 'Working hypothesis' : 'Speculative'}
        </span>
      )}
    </div>
    <div className="topic-relation__body">
      <Link href={topic.url} className="topic-relation__link text-item-title">
        {topic.title}
      </Link>
      <div className="cluster cluster-tight">
        {isUserHypothesis && (
          <span className="topic-relation__hypothesis text-label">
            Author hypothesis
          </span>
        )}
        {scope && (
          <span className="topic-relation__scope text-label">
            {scope.name}{scope.domain ? ` · ${scope.domain}` : ''}
          </span>
        )}
      </div>
      {note && (
        <div className="text-body-sm text-muted">{note}</div>
      )}
    </div>
  </div>
);

const TopicRelations = ({ outgoing, incoming }) => {
  const hasOutgoing = outgoing?.length > 0;
  const hasIncoming = incoming?.length > 0;

  if (!hasOutgoing && !hasIncoming) return null;

  return (
    <div className="topic-relations">
      {hasOutgoing && (
        <div className="block">
          <div className="block__header">
            <div className="text-block-title">This topic influences</div>
          </div>
          <div className="block__body">
            <div className="stack stack-tight">
              {outgoing.map((rel) => (
                <RelationItem
                  key={rel.id}
                  relationType={rel.relationType}
                  topic={rel.toTopic}
                  confidence={rel.confidence}
                  scope={rel.scope}
                  isUserHypothesis={rel.isUserHypothesis}
                  note={rel.note}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {hasIncoming && (
        <div className="block">
          <div className="block__header">
            <div className="text-block-title">Influenced by</div>
          </div>
          <div className="block__body">
            <div className="stack stack-tight">
              {incoming.map((rel) => (
                <RelationItem
                  key={rel.id}
                  relationType={rel.relationType}
                  topic={rel.fromTopic}
                  confidence={rel.confidence}
                  scope={rel.scope}
                  isUserHypothesis={rel.isUserHypothesis}
                  note={rel.note}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const topicShape = PropTypes.shape({
  id:    PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  url:   PropTypes.string.isRequired,
});

const relationShape = PropTypes.shape({
  id:              PropTypes.number.isRequired,
  relationType:    PropTypes.string.isRequired,
  confidence:      PropTypes.oneOf(['established', 'working_hypothesis', 'speculative']).isRequired,
  scope:           PropTypes.shape({
    id:     PropTypes.number,
    name:   PropTypes.string,
    domain: PropTypes.string,
  }),
  note:            PropTypes.string,
  isUserHypothesis: PropTypes.bool,
});

TopicRelations.propTypes = {
  outgoing: PropTypes.arrayOf(PropTypes.shape({
    ...relationShape,
    toTopic: topicShape,
  })),
  incoming: PropTypes.arrayOf(PropTypes.shape({
    ...relationShape,
    fromTopic: topicShape,
  })),
};

TopicRelations.defaultProps = {
  outgoing: [],
  incoming: [],
};

export default TopicRelations;